import { toast } from '@/components/custom-toast'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { exportOrganizationUsersCsvOrganizationOrganizationIdUsersExportGetOptions } from '@/generated/@tanstack/react-query.gen'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
interface IUserExportDataProps {
  organizationId: number
  search?: string | null
  sortBy?: string
  sortOrder?: string
  activeTab?: string
}
export default function ExportData(props: IUserExportDataProps) {
  const { organizationId, search, sortBy, sortOrder, activeTab } = props
  const [exporting, setExporting] = useState(false)
  const queryClient = useQueryClient()

  const handleExport = async (format: string) => {
    setExporting(true)

    await queryClient
      .fetchQuery({
        ...exportOrganizationUsersCsvOrganizationOrganizationIdUsersExportGetOptions(
          {
            path: { organization_id: organizationId },
            query: {
              is_csv: format === 'csv',
              search: search || undefined,
              sort_by: sortBy,
              sort_order: sortOrder,
              filter_status:
                activeTab === 'active'
                  ? '2'
                  : activeTab === 'invited'
                    ? '1'
                    : activeTab === 'all'
                      ? undefined
                      : '0',
            },
          }
        ),
      })
      .then((data) => {
        let blob = new Blob([data as string], {
          type: 'text/csv',
        })

        if (format === 'xlsx') {
          blob = new Blob([data as string], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          })
        }

        const now = new Date()
        const datetime = now.toLocaleString('sv-SE').replace(/[^0-9]/g, '')
        const formattedDatetime = `${datetime.slice(0, 8)}T${datetime.slice(8, 14)}`
        const fileName = `hydra_score_users_export_${formattedDatetime}.${format}`
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.style.display = 'none'
        a.href = url
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        toast.success({
          title: 'Success',
          message: `Users exported successfully as ${format.toUpperCase()}.`,
        })
      })
      .catch((error) => {
        toast.error({
          title: 'Error',
          message:
            (error.response?.data.detail as unknown as string) ?? error.message,
        })
      })
      .finally(() => {
        setExporting(false)
      })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={'sm'}
          className="flex items-center text-primary gap-2 text-base font-medium"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.80665 5.14001L7.33331 3.60668V10C7.33331 10.1768 7.40355 10.3464 7.52858 10.4714C7.6536 10.5964 7.82317 10.6667 7.99998 10.6667C8.17679 10.6667 8.34636 10.5964 8.47138 10.4714C8.59641 10.3464 8.66665 10.1768 8.66665 10V3.60668L10.1933 5.14001C10.2553 5.2025 10.329 5.25209 10.4103 5.28594C10.4915 5.31979 10.5786 5.33721 10.6666 5.33721C10.7547 5.33721 10.8418 5.31979 10.923 5.28594C11.0043 5.25209 11.078 5.2025 11.14 5.14001C11.2025 5.07804 11.2521 5.0043 11.2859 4.92306C11.3198 4.84182 11.3372 4.75469 11.3372 4.66668C11.3372 4.57867 11.3198 4.49153 11.2859 4.41029C11.2521 4.32906 11.2025 4.25532 11.14 4.19335L8.47331 1.52668C8.40991 1.46599 8.33515 1.41841 8.25331 1.38668C8.09101 1.32 7.90895 1.32 7.74665 1.38668C7.66481 1.41841 7.59005 1.46599 7.52665 1.52668L4.85998 4.19335C4.79782 4.2555 4.74851 4.3293 4.71487 4.41051C4.68123 4.49173 4.66392 4.57877 4.66392 4.66668C4.66392 4.75459 4.68123 4.84163 4.71487 4.92285C4.74851 5.00406 4.79782 5.07785 4.85998 5.14001C4.92214 5.20217 4.99593 5.25148 5.07715 5.28512C5.15836 5.31876 5.24541 5.33607 5.33331 5.33607C5.42122 5.33607 5.50827 5.31876 5.58948 5.28512C5.67069 5.25148 5.74449 5.20217 5.80665 5.14001ZM14 9.33335C13.8232 9.33335 13.6536 9.40358 13.5286 9.52861C13.4036 9.65363 13.3333 9.8232 13.3333 10V12.6667C13.3333 12.8435 13.2631 13.0131 13.1381 13.1381C13.013 13.2631 12.8435 13.3333 12.6666 13.3333H3.33331C3.1565 13.3333 2.98693 13.2631 2.86191 13.1381C2.73688 13.0131 2.66665 12.8435 2.66665 12.6667V10C2.66665 9.8232 2.59641 9.65363 2.47138 9.52861C2.34636 9.40358 2.17679 9.33335 1.99998 9.33335C1.82317 9.33335 1.6536 9.40358 1.52858 9.52861C1.40355 9.65363 1.33331 9.8232 1.33331 10V12.6667C1.33331 13.1971 1.54403 13.7058 1.9191 14.0809C2.29417 14.456 2.80288 14.6667 3.33331 14.6667H12.6666C13.1971 14.6667 13.7058 14.456 14.0809 14.0809C14.4559 13.7058 14.6666 13.1971 14.6666 12.6667V10C14.6666 9.8232 14.5964 9.65363 14.4714 9.52861C14.3464 9.40358 14.1768 9.33335 14 9.33335Z"
              fill="#00B69B"
            />
          </svg>
          {exporting ? 'Exporting...' : 'Export'}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="right-2 relative">
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={() => handleExport('xlsx')}
        >
          Microsoft Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer"
          onClick={() => handleExport('csv')}
        >
          Comma Separated Values (.csv)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
